import {
  LocalAudioTrack,
  Participant,
  ParticipantEvent,
  Track,
} from "livekit-client";
import { Accessor, createSignal, onCleanup } from "solid-js";
import { useIsSpeaking } from "solid-livekit-components";

import { useVoice } from "./state";

/**
 * Konkord: speaking indicator computed from the audio itself
 *
 * LiveKit's own `isSpeaking` comes from the server's active speaker updates,
 * which arrive about half a second late and switch off late too. Here every
 * microphone track goes through an analyser and the ring lights up on the
 * first loud enough frame, then stays on briefly so it doesn't flicker
 * between words.
 */

/** RMS level (0..1) that counts as talking, about -42 dBFS */
const SPEAKING_LEVEL = 0.008;
/** Keep the indicator on for this long after the voice drops */
const HOLD_MS = 250;
const POLL_MS = 40;

type Meter = {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  buffer: Float32Array<ArrayBuffer>;
  speaking: Accessor<boolean>;
  setSpeaking: (value: boolean) => void;
  lastLoud: number;
  users: number;
};

let context: AudioContext | undefined;

// a call restored on page load starts without a user gesture, so the context
// may begin suspended; resume it on the first interaction
document.addEventListener(
  "pointerdown",
  () => {
    // (only while there is something to listen to, see release)
    if (context?.state === "suspended" && meters.size) void context.resume();
  },
  { capture: true },
);
let timer: ReturnType<typeof setInterval> | undefined;
const meters = new Map<string, Meter>();

function tick() {
  // Konkord: nobody sees the rings in a hidden tab
  if (document.hidden) return;
  const now = performance.now();
  for (const meter of meters.values()) {
    meter.analyser.getFloatTimeDomainData(meter.buffer);
    let sum = 0;
    for (const sample of meter.buffer) sum += sample * sample;
    const level = Math.sqrt(sum / meter.buffer.length);

    if (level >= SPEAKING_LEVEL) meter.lastLoud = now;
    const speaking = now - meter.lastLoud < HOLD_MS;
    if (speaking !== meter.speaking()) meter.setSpeaking(speaking);
  }
}

/**
 * Shared meter for a media stream track, released when no one uses it anymore
 */
function acquire(track: MediaStreamTrack): Meter | undefined {
  let meter = meters.get(track.id);
  if (!meter) {
    try {
      context ??= new AudioContext();
      if (context.state === "suspended") void context.resume();

      const source = context.createMediaStreamSource(new MediaStream([track]));
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);

      const [speaking, setSpeaking] = createSignal(false);
      meter = {
        source,
        analyser,
        buffer: new Float32Array(analyser.fftSize),
        speaking,
        setSpeaking,
        lastLoud: -Infinity,
        users: 0,
      };
      meters.set(track.id, meter);
      timer ??= setInterval(tick, POLL_MS);
    } catch (err) {
      console.warn("[konkord] speaking meter unavailable", err);
      return;
    }
  }

  meter.users++;
  return meter;
}

function release(track: MediaStreamTrack) {
  const meter = meters.get(track.id);
  if (!meter || --meter.users > 0) return;

  meter.source.disconnect();
  meters.delete(track.id);
  if (!meters.size) {
    clearInterval(timer);
    timer = undefined;
    // Konkord: no call, no audio thread kept running (acquire resumes it)
    if (context?.state === "running") void context.suspend();
  }
}

/**
 * Whether a participant is talking right now, following their microphone track
 * (falls back to LiveKit's indicator while there is no track to listen to)
 */
export function useFastSpeaking(participant: Participant): Accessor<boolean> {
  const fallback = useIsSpeaking(participant);
  const voice = useVoice();
  const [meter, setMeter] = createSignal<Meter>();
  let current: MediaStreamTrack | undefined;

  const update = () => {
    const publication = participant.getTrackPublication(
      Track.Source.Microphone,
    );
    const source = publication?.isMuted ? undefined : publication?.track;
    // our own microphone: listen after noise suppression, like the others hear it
    const track =
      source instanceof LocalAudioTrack
        ? (source.getProcessor()?.processedTrack ?? source.mediaStreamTrack)
        : source?.mediaStreamTrack;
    if (track === current) return;

    if (current) release(current);
    current = track;
    setMeter(track ? acquire(track) : undefined);
  };

  const events = [
    ParticipantEvent.TrackPublished,
    ParticipantEvent.TrackUnpublished,
    ParticipantEvent.TrackSubscribed,
    ParticipantEvent.TrackUnsubscribed,
    ParticipantEvent.TrackMuted,
    ParticipantEvent.TrackUnmuted,
    ParticipantEvent.LocalTrackPublished,
    ParticipantEvent.LocalTrackUnpublished,
  ] as const;

  for (const event of events) participant.on(event, update);
  update();
  // the noise suppression processor attaches after publishing, without an event
  const recheck = setInterval(() => {
    if (!document.hidden) update();
  }, 1000);

  onCleanup(() => {
    clearInterval(recheck);
    for (const event of events) participant.off(event, update);
    if (current) release(current);
    current = undefined;
  });

  return () => {
    const active = meter();
    // a suspended context only reads silence
    // remote audio doesn't reach the analyser while the browser blocks playback
    const audible = participant.isLocal || !voice.audioBlocked();
    if (active && audible && context?.state === "running") {
      return active.speaking();
    }
    // muted or no track: never speaking
    const publication = participant.getTrackPublication(
      Track.Source.Microphone,
    );
    return !!publication && !publication.isMuted && fallback();
  };
}
